import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import gsap from 'gsap';
import { clamp } from 'three/src/math/MathUtils';

import { sections } from '../../common/config';
import useScrollStore from '../../common/store/useScrollStore';
import { getThresholds, getTranslateY } from './utils';

const useScrollControl = (
  wrapper: React.RefObject<HTMLDivElement>,
  scroll: React.RefObject<HTMLDivElement>,
  canvas: React.RefObject<HTMLDivElement>
) => {
  const setPosition = useScrollStore(useCallback(state => state.setPosition, []));
  const setScrollTo = useScrollStore(useCallback(state => state.setScrollTo, []));

  const position = useRef(0);

  const thresholds = useMemo(() => getThresholds(), [sections]);
  const minHeight = useMemo(() => -0.1, [])

  const getMaxHeight = useCallback(() => {
    if (wrapper.current)
      return wrapper.current.clientHeight - window.innerHeight;
    else
      return 0;
  }, []);

  const getPercentageHeight = useCallback((position: number) => {
    return -100 + (position / getMaxHeight() * 100);
  }, []);

  const scrollTo = useCallback((position: number, immediate: boolean) => {
    const defaults = {
      duration: immediate ? 0 : 1,
      ease: "power2.out",
      overwrite: true
    };

    gsap.to(wrapper.current, {
      ...defaults,
      y: position,
      onUpdate: () => { wrapper.current && setPosition(getTranslateY(wrapper.current)) }
    });

    gsap.to(scroll.current, {
      ...defaults,
      y: `${getPercentageHeight(-position)}%`
    });

    gsap.to(canvas.current, {
      ...defaults,
      y: -position
    });
  }, []);

  /**
   * onMount
   */
  useEffect(() => {
    setScrollTo && setScrollTo((p: number, immediate: boolean) => {
      position.current = p;
      scrollTo(p, immediate);
    });
  }, [setScrollTo]);

  /**
   * onWheel
   */
  useEffect(() => {
    function handleWheel(e: WheelEvent) {
      if (!useScrollStore.getState().isLocked) {
        position.current = clamp(position.current - (e.deltaY / 3.5), -getMaxHeight(), minHeight);
        scrollTo(position.current, false);
      }
    }

    window.addEventListener("wheel", handleWheel);
    return () => { window.removeEventListener("wheel", handleWheel) }
  }, []);

  /**
   * onTouch
   */
  useEffect(() => {
    let startPosition = 0;
    let movePosition = 0;

    function handleTouchStart(e: TouchEvent) {
      startPosition = e.touches[0].clientY;
      movePosition = clamp(position.current, -getMaxHeight(), minHeight);
    }

    function handleTouchMove(e: TouchEvent) {
      if (!useScrollStore.getState().isLocked) {
        const delta = e.touches[0].clientY - startPosition;
        movePosition = clamp(position.current + delta, -getMaxHeight(), minHeight);

        scrollTo(movePosition, false);
      }
    }

    function handleTouchEnd() {
      position.current = movePosition;
    }

    window.addEventListener("touchstart", handleTouchStart, { passive: false });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    }
  }, []);

  /**
   * onResize
   */
  useEffect(() => {
    function handleResize() {
      position.current = clamp(position.current, -getMaxHeight(), minHeight);
      scrollTo(position.current, true);
    }

    window.addEventListener("resize", handleResize);
    return () => { window.removeEventListener("resize", handleResize) }
  }, []);
}

export default useScrollControl;